import React, { useEffect, useMemo, useState } from 'react';
import KickImage from 'Reuseables/KickImage';
import Card from '../../Atoms/Card/Card';
import Section from '../../Molecules/Section';
import Styles from './OneRowCollectionSection.module.scss';
import { useFetchProductData } from 'Pages/Home/Hooks/useFetchProductData';
import { useHistory } from 'react-router';
import Image from '../../Atoms/Image';

import ThunderIcon from '../../../../../images/icons/thunder-icon.png';
import RightArrow from '../../../../../images/arrow-right-chevron.png';
import LeftArrow from '../../../../../images/arrow-left-chevron.png';
import { getCategoryName } from 'Pages/Home/Helper/Formatter';
import { useFetchCollectionData } from 'Pages/Home/Hooks/useFetchCollectionData';
import { IoMdReturnLeft } from 'react-icons/io';

type TOneRowCollectionSectionProps = {
  className?: string;
  slug?: string;
  category?: string;
  title?: string;
  subtitle?: string;
  actionText?: string;
  isPreverifiedCollection?: boolean;
  userCurrency: any;
};

const OneRowCollectionSection: React.FC<TOneRowCollectionSectionProps> = ({
  className,
  slug,
  category,
  title,
  subtitle,
  actionText,
  isPreverifiedCollection,
  userCurrency,
}) => {
  const history = useHistory();
  const [scrollLeftValue, setScrollLeftValue] = useState<number>(0);
  const { data: products } = slug
    ? useFetchCollectionData(
        category ? category : 'all_category',
        slug,
        16,
        isPreverifiedCollection,
      )
    : useFetchProductData('pre-verified', 'all_category', 16, 300);
  const productScroll = React.useRef<HTMLDivElement>(null);

  const wrapperClassName = useMemo(() => {
    return className ? className : Styles['wrapper'];
  }, [className]);

  const renderItems = useMemo(() => {
    if (!products?.length) {
      return null;
    }

    return products.map((item, index) => {
      const onClickProduct = () => {
        if (isPreverifiedCollection) {
          history.push(
            `/${getCategoryName(item.category)}/${item.brand}/${
              item.slug
            }?collection=${slug}&express_shipping=true&from=home`,
          );
          return;
        }
        history.push(
          `/${getCategoryName(item.category)}/${item.brand}/${item.slug}?from=home`,
        );
      };
      return (
        <div key={`fast-delivery-card-${index}`}>
          <Card.Product
            {...item}
            onClick={onClickProduct}
            lastProduct={index === products.length - 1}
            userCurrency={userCurrency}
          />
        </div>
      );
    });
  }, [products]);

  const onClickViewAll = () => {
    if (slug) {
      if (category && category !== 'all' && category !== 'all_category') {
        window.open(
          `/${getCategoryName(category)}/collection/${slug}${
            isPreverifiedCollection
              ? '?sneakers_condition=express_shipping&sort_by=price_asc&express_shipping=true'
              : ''
          }`,
          '_blank',
        );
        return;
      }
      window.open(
        `/collection/${slug}${
          isPreverifiedCollection
            ? '?sneakers_condition=express_shipping&sort_by=price_asc&express_shipping=true'
            : ''
        }`,
        '_blank',
      );
      return;
    }
    window.open('/browse-all?sneakers_condition=express_shipping&sort_by=available_size', '_blank');
  };

  const rightArrowClicked = () => {
    if (!productScroll.current) {
      return;
    }

    const scrolledNow = Math.ceil(productScroll.current.scrollLeft);
    const maxScrollWidth =
      productScroll.current.scrollWidth - productScroll.current.clientWidth;

    if (scrolledNow >= maxScrollWidth) {
      return;
    }

    const interval = setInterval(() => {
      if (!productScroll.current) {
        return;
      }

      productScroll.current.scrollLeft += 4;
    }, 1);

    setTimeout(() => {
      clearInterval(interval);

      if (!productScroll.current) {
        return;
      }

      setScrollLeftValue(Math.ceil(productScroll.current.scrollLeft));
    }, 1000);
  };

  const leftArrowClicked = () => {
    if (!productScroll.current) {
      return;
    }

    const scrolledNow = Math.ceil(productScroll.current.scrollLeft);
    const maxScrollWidth = 0;

    if (scrolledNow <= maxScrollWidth) {
      return;
    }

    const interval = setInterval(() => {
      if (!productScroll.current) {
        return;
      }

      productScroll.current.scrollLeft -= 4;
    }, 1);

    setTimeout(() => {
      clearInterval(interval);

      if (!productScroll.current) {
        return;
      }

      setScrollLeftValue(Math.ceil(productScroll.current.scrollLeft));
    }, 1000);
  };

  const isShowingArrow = (arrow: 'left' | 'right'): boolean => {
    if (!productScroll.current) {
      return false;
    }

    if (
      productScroll.current.scrollWidth <= productScroll.current.clientWidth
    ) {
      return false;
    }

    if (arrow === 'left') {
      return scrollLeftValue > 0;
    }

    if (arrow === 'right') {
      const maxScrollWidth =
        productScroll.current.scrollWidth - productScroll.current.clientWidth;
      return scrollLeftValue < maxScrollWidth;
    }

    return false;
  };

  const onScrollItems = () => {
    if (!productScroll.current) {
      return;
    }

    const {
      current: { scrollWidth, clientWidth, scrollLeft },
    } = productScroll;

    const scrollLeftCeil = Math.ceil(scrollLeft);
    const maxScrollWidth = scrollWidth - clientWidth;

    if (scrollLeftCeil % 100 === 0) {
      setScrollLeftValue(scrollLeftCeil);
    }

    if (scrollLeftCeil >= maxScrollWidth) {
      setScrollLeftValue(scrollLeftCeil);
    }

    if (scrollLeft === 0) {
      setScrollLeftValue(0);
    }
  };

  if (!products || !products?.length) {
    return <div />;
  }

  return (
    <div className={wrapperClassName}>
      <Section
        title={
          slug ? (
            title
          ) : (
            <span>
              {title}
              <KickImage
                src={ThunderIcon}
                height={16}
                width={10}
                style={{
                  marginLeft: 4,
                  marginRight: '-4px',
                  marginBottom: 4,
                }}
              />
            </span>
          )
        }
        subTitle={subtitle ?? 'Get them in a couple of days'}
        actionText={actionText ? actionText : 'View All'}
        onAction={onClickViewAll}
        withPaddingTitle
        lessMarginTopOnSubtitle={!slug}
        spacerHeight={32}
      >
        <div
          ref={productScroll}
          onScroll={onScrollItems}
          className={Styles['product-wrapper']}
        >
          {renderItems}
        </div>

        {isShowingArrow('left') && (
          <div
            className={Styles['left-arrow-container']}
            onClick={leftArrowClicked}
          >
            <Image src={LeftArrow} className={Styles['left-arrow']} />
          </div>
        )}

        {isShowingArrow('right') && (
          <div
            className={Styles['right-arrow-container']}
            onClick={rightArrowClicked}
          >
            <Image src={RightArrow} className={Styles['right-arrow']} />
          </div>
        )}
      </Section>
    </div>
  );
};

export default OneRowCollectionSection;
